import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const FIXED_ITEMS = [
  {
    id: 'workplacenl',
    label: 'WorkplaceNL Letter of Good Standing',
    category: 'Insurance & Legal',
    frequency: 'Every 45 days',
    description: 'Town may request updated certificate at any time. Submit proactively.',
    contact: 'Jason Lawrence',
    renewalDays: 45,
  },
  {
    id: 'liability_insurance',
    label: 'Liability Insurance Certificate',
    category: 'Insurance & Legal',
    frequency: 'Annual',
    description: 'Policy expires June 2026. Town must be named as additional insured.',
    contact: 'Charlene Victorino',
    renewalDays: 365,
  },
  {
    id: 'weekly_checklists',
    label: 'Weekly Checklist Submission to Town',
    category: 'Ongoing Operations',
    frequency: 'Weekly',
    description: 'All worker checklists compiled and submitted to Jason Lawrence weekly. Failure = negative report.',
    contact: 'Jason Lawrence',
    renewalDays: 7,
  },
  {
    id: 'site_visits',
    label: 'Bi-Weekly Supervisor Site Visits',
    category: 'Ongoing Operations',
    frequency: 'Every 2 weeks',
    description: 'Supervisor must visit all buildings every 2 weeks to verify work quality.',
    contact: 'Internal',
    renewalDays: 14,
  },
  {
    id: 'leed_products',
    label: 'LEED Product Compliance — Buildings E & F',
    category: 'Ongoing Operations',
    frequency: 'Ongoing',
    description: 'Green Seal/EcoLogo certified products only. Non-compliance = immediate negative report.',
    contact: 'Jason Lawrence',
    renewalDays: null,
  },
];

// Parse a plain 'YYYY-MM-DD' string as a *local* date, not UTC — new
// Date('2026-08-20') parses as UTC midnight, which for Newfoundland's
// negative offset is the previous local evening, making a comparison
// against a local-midnight "today" off by a fragile fraction of a day
// depending on the hour (happened to round out correctly before, but
// wasn't guaranteed to).
function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = parseLocalDate(dateStr);
  return Math.ceil((target - today) / (1000*60*60*24));
}

function statusFromDays(days) {
  if (days === null) return 'ongoing';
  if (days < 0) return 'overdue';
  if (days <= 7) return 'urgent';
  if (days <= 14) return 'warning';
  return 'ok';
}

function statusColor(status) {
  if (status === 'overdue') return '#B33A2E';
  if (status === 'urgent') return '#B5730A';
  if (status === 'warning') return '#96600A';
  if (status === 'ongoing') return '#1F3A47';
  return '#2F7A4F';
}

function statusLabel(days, status) {
  if (status === 'ongoing') return 'Ongoing';
  if (status === 'overdue') return `${Math.abs(days)}d overdue`;
  if (days == null) return 'Set a due date';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `${days}d remaining`;
}

export default function ComplianceCentre() {
  const [records, setRecords] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState({ item_id: '', label: '', due_date: '', notes: '', worker_id: '', worker_name: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const [recRes, workerRes] = await Promise.all([
      supabase.from('compliance_records').select('*').order('due_date', { ascending: true }),
      supabase.from('profiles').select('id, full_name, role, conduct_check, vulnerable_sector, whmis, secure_access').in('role', ['worker', 'supervisor']),
    ]);
    if (recRes.data) setRecords(recRes.data);
    if (workerRes.data) setWorkers(workerRes.data);
    setLoading(false);
  }

  async function handleSave() {
    if (!form.label || !form.due_date) return;
    setSaving(true);
    setSaveError(null);
    // Only write the fields this form actually edits, instead of round-
    // tripping the entire record object (which for "Edit" starts as a full
    // copy of the DB row) — that used to overwrite id/created_at/etc. with
    // stale values on every save, clobbering any concurrent change to a
    // field this form doesn't expose.
    // compliance_records.status has a DB check constraint allowing only
    // 'pending' | 'complete' | 'overdue' — the richer on-screen status
    // ('urgent'/'warning'/'ongoing', from days-until-due) is a display-only
    // concept computed from due_date, not something this column tracks.
    const payload = {
      item_id: form.item_id || null,
      label: form.label,
      due_date: form.due_date,
      notes: form.notes || null,
      renewal_days: form.renewal_days ?? null,
      worker_id: form.worker_id || null,
      worker_name: form.worker_name || null,
      status: daysUntil(form.due_date) < 0 ? 'overdue' : 'pending',
    };
    const { error } = editingRecord
      ? await supabase.from('compliance_records').update(payload).eq('id', editingRecord.id)
      : await supabase.from('compliance_records').insert([payload]);
    if (error) {
      setSaveError(error.message);
      setSaving(false);
      return;
    }
    await loadData();
    setShowForm(false);
    setSaving(false);
  }

  async function handleComplete(record) {
    setActionError(null);
    const nextDue = record.renewal_days
      ? new Date(Date.now() + record.renewal_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      : null;
    const { error } = await supabase.from('compliance_records').update({
      last_completed: new Date().toISOString().split('T')[0],
      due_date: nextDue,
      // Recurring item (renewal_days set): a new cycle starts, so it's
      // 'pending' again toward the next due date. One-off item: it's done.
      status: nextDue ? 'pending' : 'complete',
    }).eq('id', record.id);
    if (error) { setActionError(error.message); return; }
    await loadData();
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this compliance record?')) return;
    setActionError(null);
    const { error } = await supabase.from('compliance_records').delete().eq('id', id);
    if (error) { setActionError(error.message); return; }
    await loadData();
  }

  // Merge fixed items with database records
  const fixedWithStatus = FIXED_ITEMS.map(item => {
    const record = records.find(r => r.item_id === item.id);
    const days = record ? daysUntil(record.due_date) : null;
    const status = record ? statusFromDays(days) : item.renewalDays ? 'warning' : 'ongoing';
    return { ...item, record, days, status };
  });

  const workerItems = workers.map(w => ({
    id: w.id,
    name: w.full_name,
    conduct: w.conduct_check,
    vulnerable: w.vulnerable_sector,
    whmis: w.whmis,
    access: w.secure_access,
    score: [w.conduct_check, w.vulnerable_sector, w.whmis, w.secure_access].filter(Boolean).length,
  }));

  const customRecords = records.filter(r => !FIXED_ITEMS.find(f => f.id === r.item_id));
  const overdueCount = fixedWithStatus.filter(i => i.status === 'overdue' || i.status === 'urgent').length;
  const workersClear = workerItems.filter(w => w.score === 4).length;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Compliance Centre</div>
          <div style={s.subtitle}>Contract TOP-25-27 · Town of Paradise</div>
        </div>
        <button style={s.addBtn} onClick={() => { setForm({ item_id: '', label: '', due_date: '', notes: '', worker_id: '', worker_name: '', renewal_days: null }); setEditingRecord(null); setShowForm(true); }}>
          + Add Item
        </button>
      </div>

      {/* Summary chips — held back until the data is in, since rendering
          "0 urgent items" mid-load reads as a genuine all-clear. */}
      <div style={s.chips}>
        {loading ? (
          <div style={s.chip}><span style={{ color: '#5B6B72' }}>Checking compliance…</span></div>
        ) : (
          <>
            <div style={{ ...s.chip, borderColor: overdueCount > 0 ? '#B33A2E' : '#2F7A4F' }}>
              <span style={{ color: overdueCount > 0 ? '#B33A2E' : '#2F7A4F', fontWeight: 800 }}>{overdueCount}</span>
              <span style={{ color: '#5B6B72' }}> needing attention</span>
            </div>
            <div style={s.chip}>
              <span style={{ color: '#2F7A4F', fontWeight: 800 }}>{workersClear}</span>
              <span style={{ color: '#5B6B72' }}>/{workerItems.length} workers fully cleared</span>
            </div>
          </>
        )}
      </div>

      {actionError && (
        <div style={s.errorBanner}>{actionError} <button style={s.errorDismiss} onClick={() => setActionError(null)}>✕</button></div>
      )}

      {/* Tabs */}
      <div style={s.tabRow}>
        {['overview', 'workers', 'custom'].map(tab => (
          <button key={tab} style={activeTab === tab ? { ...s.tab, color: '#f97316', borderBottom: '2px solid #f97316' } : s.tab}
            onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? 'Contract Requirements' : tab === 'workers' ? 'Worker Clearances' : 'Custom Items'}
          </button>
        ))}
      </div>

      {loading ? <div style={s.loading}>Loading...</div> : (
        <>
          {activeTab === 'overview' && (
            <div style={s.list}>
              {fixedWithStatus.map(item => (
                <div key={item.id} style={{ ...s.card, borderLeftColor: statusColor(item.status) }}>
                  <div style={s.cardTop}>
                    <div style={s.cardLeft}>
                      <div style={s.cardCategory}>{item.category}</div>
                      <div style={s.cardLabel}>{item.label}</div>
                      <div style={s.cardDesc}>{item.description}</div>
                      {item.record?.last_completed && (
                        <div style={s.cardMeta}>Last completed: {item.record.last_completed}</div>
                      )}
                    </div>
                    <div style={s.cardRight}>
                      <div style={{ ...s.statusBadge, color: statusColor(item.status) }}>
                        {statusLabel(item.days, item.status)}
                      </div>
                      <div style={s.freq}>{item.frequency}</div>
                    </div>
                  </div>
                  {item.record && (
                    <div style={s.cardActions}>
                      <button style={s.completeBtn} onClick={() => handleComplete(item.record)}>✓ Mark Renewed</button>
                      {item.record.notes && <div style={s.recordNote}>{item.record.notes}</div>}
                    </div>
                  )}
                  {!item.record && item.renewalDays && (
                    <div style={s.cardActions}>
                      <button style={s.setDateBtn} onClick={() => {
                        setForm({ item_id: item.id, label: item.label, due_date: '', notes: '', renewal_days: item.renewalDays });
                        setEditingRecord(null);
                        setShowForm(true);
                      }}>Set Due Date</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'workers' && (
            <div style={s.list}>
              {workerItems.length === 0 && <div style={s.empty}>No workers added yet. Add workers in the Staff tab.</div>}
              {workerItems.map(w => (
                <div key={w.id} style={{ ...s.card, borderLeftColor: w.score === 4 ? '#2F7A4F' : w.score >= 2 ? '#B5730A' : '#B33A2E' }}>
                  <div style={s.cardTop}>
                    <div style={s.cardLeft}>
                      <div style={s.cardLabel}>{w.name}</div>
                      <div style={s.clearanceRow}>
                        {[
                          { key: 'conduct', label: 'Certificate of Conduct' },
                          { key: 'vulnerable', label: 'Vulnerable Sector' },
                          { key: 'whmis', label: 'WHMIS' },
                          { key: 'access', label: 'Secure Access' },
                        ].map(item => (
                          <span key={item.key} style={{
                            ...s.clearChip,
                            background: w[item.key] ? 'rgba(47,122,79,0.10)' : 'rgba(91,107,114,0.08)',
                            color: w[item.key] ? '#2F7A4F' : '#5B6B72',
                          }}>
                            {w[item.key] ? '✓' : '○'} {item.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ ...s.statusBadge, color: w.score === 4 ? '#2F7A4F' : w.score >= 2 ? '#B5730A' : '#B33A2E' }}>
                      {w.score}/4 cleared
                    </div>
                  </div>
                  {w.score < 4 && (
                    <div style={{ ...s.cardDesc, color: '#96600A', marginTop: 6 }}>
                      ⚠️ Update clearances in the Staff tab once documents are received.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'custom' && (
            <div style={s.list}>
              {customRecords.length === 0 && (
                <div style={s.empty}>No custom items yet. Use "+ Add Item" to track any additional compliance requirements.</div>
              )}
              {customRecords.map(record => {
                const days = daysUntil(record.due_date);
                const status = statusFromDays(days);
                return (
                  <div key={record.id} style={{ ...s.card, borderLeftColor: statusColor(status) }}>
                    <div style={s.cardTop}>
                      <div style={s.cardLeft}>
                        <div style={s.cardLabel}>{record.label}</div>
                        {record.notes && <div style={s.cardDesc}>{record.notes}</div>}
                        {record.last_completed && <div style={s.cardMeta}>Last completed: {record.last_completed}</div>}
                      </div>
                      <div style={s.cardRight}>
                        <div style={{ ...s.statusBadge, color: statusColor(status) }}>{statusLabel(days, status)}</div>
                      </div>
                    </div>
                    <div style={s.cardActions}>
                      <button style={s.completeBtn} onClick={() => handleComplete(record)}>✓ Mark Done</button>
                      <button style={s.editBtn} onClick={() => { setForm(record); setEditingRecord(record); setShowForm(true); }}>Edit</button>
                      <button style={s.deleteBtn} onClick={() => handleDelete(record.id)}>Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showForm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{editingRecord ? 'Edit Item' : 'Add Compliance Item'}</div>
              <button style={s.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <label style={s.label}>ITEM NAME *</label>
              <input style={s.input} value={form.label} placeholder="e.g. Insurance Renewal"
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))} />
              <label style={s.label}>DUE DATE *</label>
              <input style={s.input} type="date" value={form.due_date}
                onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
              <label style={s.label}>NOTES</label>
              <textarea style={s.textarea} rows={3} value={form.notes}
                placeholder="Any relevant details..."
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            {saveError && <div style={{ ...s.errorBanner, margin: '0 20px 10px' }}>{saveError}</div>}
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={(!form.label || !form.due_date || saving) ? { ...s.saveBtn, opacity: 0.5 } : s.saveBtn}
                disabled={!form.label || !form.due_date || saving}
                onClick={handleSave}>{saving ? 'Saving...' : editingRecord ? 'Save Changes' : 'Add Item'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '0 0 60px', background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 12px' },
  title: { fontSize: 22, fontWeight: 800, color: '#1A2B33', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 13, color: '#5B6B72', marginTop: 2 },
  addBtn: { background: '#f97316', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  chips: { display: 'flex', gap: 8, padding: '0 20px 12px', flexWrap: 'wrap' },
  chip: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 20, padding: '6px 14px', fontSize: 13 },
  tabRow: { display: 'flex', borderBottom: '1px solid #E4E0D8', margin: '0 0 4px' },
  tab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#5B6B72', padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  list: { display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' },
  card: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderLeft: '3px solid', borderRadius: 12, padding: 16 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  cardLeft: { flex: 1 },
  cardRight: { flexShrink: 0, textAlign: 'right' },
  cardCategory: { fontSize: 10, fontWeight: 700, letterSpacing: '1px', color: '#5B6B72', marginBottom: 4, textTransform: 'uppercase' },
  cardLabel: { fontSize: 15, fontWeight: 700, color: '#1A2B33', marginBottom: 4 },
  cardDesc: { fontSize: 12, color: '#5B6B72', lineHeight: 1.5 },
  cardMeta: { fontSize: 11, color: '#5B6B72', marginTop: 4 },
  cardActions: { display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' },
  statusBadge: { fontSize: 12, fontWeight: 800 },
  freq: { fontSize: 11, color: '#5B6B72', marginTop: 4 },
  completeBtn: { background: 'rgba(47,122,79,0.10)', border: '1px solid #2F7A4F', color: '#2F7A4F', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  setDateBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
  editBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
  deleteBtn: { background: 'transparent', border: 'none', color: '#5B6B72', fontSize: 12, cursor: 'pointer' },
  recordNote: { fontSize: 12, color: '#5B6B72', fontStyle: 'italic' },
  clearanceRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  clearChip: { fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600 },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  errorBanner: { background: '#FBF1EF', border: '1px solid #B33A2E', color: '#B33A2E', borderRadius: 8, padding: '10px 14px', margin: '0 20px 12px', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  errorDismiss: { background: 'none', border: 'none', color: '#B33A2E', cursor: 'pointer', fontSize: 13 },
  empty: { padding: 24, textAlign: 'center', color: '#5B6B72', fontSize: 14, border: '1px dashed #E4E0D8', borderRadius: 10 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modal: { background: '#FFFFFF', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 12px', borderBottom: '1px solid #E4E0D8' },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#1A2B33' },
  closeBtn: { background: 'none', border: 'none', color: '#5B6B72', fontSize: 20, cursor: 'pointer' },
  modalBody: { padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  modalFooter: { padding: '14px 20px', borderTop: '1px solid #E4E0D8', display: 'flex', gap: 10 },
  label: { fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#5B6B72' },
  input: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '11px 14px', color: '#1A2B33', fontSize: 15, outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '11px 14px', color: '#1A2B33', fontSize: 14, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  cancelBtn: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  saveBtn: { flex: 2, background: '#f97316', border: 'none', color: '#FFFFFF', borderRadius: 8, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
};
